import { useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const TYPES = ["Particulier", "Professionnel", "Apporteur d'affaires", "Promoteur", "Syndic", "Entreprise"];
const EMPTY_FORM = { nom: "", type: "Particulier", ville: "", email: "", telephone: "", familles_interet: [] };

export default function ClientQuickCreate({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [familles, setFamilles] = useState([]);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_FORM });
      api.get("/familles")
        .then((r) => setFamilles(r.data || []))
        .catch(() => setFamilles([]));
    }
  }, [open]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const create = async () => {
    const nom = form.nom.trim();
    if (!nom) return toast.error("Nom / raison sociale requis");

    setSaving(true);
    try {
      const { data } = await api.post("/clients", {
        ...form,
        nom,
        ville: form.ville.trim(),
        email: form.email.trim(),
        telephone: form.telephone.trim(),
        contacts: [],
      });
      toast.success("Client créé");
      onOpenChange(false);
      onCreated && onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de créer le client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111827] border-[#1E293B] text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-amber-400" />
            Nouveau client
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 space-y-1.5">
            <Label className="text-slate-400 text-xs">Nom / Raison sociale *</Label>
            <Input autoFocus data-testid="devis-new-client-nom" value={form.nom} onChange={(e) => update("nom", e.target.value)} placeholder="Ex. Jean Dupont / Dupont SARL" className="bg-[#0B0F17] border-[#1E293B]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-400 text-xs">Type</Label>
            <Select value={form.type} onValueChange={(v) => update("type", v)}>
              <SelectTrigger data-testid="devis-new-client-type" className="bg-[#0B0F17] border-[#1E293B]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                {TYPES.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 space-y-2">
            <Label className="text-slate-400 text-xs">Familles d’intérêt</Label>
            <div className="flex flex-wrap gap-2">
              {familles.map((famille) => {
                const selected = form.familles_interet?.includes(famille.nom);

                return (
                  <button
                    key={famille.id}
                    type="button"
                    onClick={() =>
                      update(
                        "familles_interet",
                        selected
                          ? form.familles_interet.filter((f) => f !== famille.nom)
                          : [...(form.familles_interet || []), famille.nom]
                      )
                    }
                    className={`rounded-full px-3 py-1.5 text-xs font-medium border transition ${
                      selected
                        ? "bg-blue-500/15 border-blue-400 text-blue-300"
                        : "bg-[#0B0F17] border-[#1E293B] text-slate-400 hover:border-slate-500 hover:text-slate-200"
                    }`}
                  >
                    {famille.nom}
                  </button>
                );
              })}
            </div>

            {form.familles_interet?.length > 0 && (
              <p className="text-[11px] text-slate-500">
                {form.familles_interet.length} famille{form.familles_interet.length > 1 ? "s" : ""} sélectionnée{form.familles_interet.length > 1 ? "s" : ""}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-slate-400 text-xs">Ville</Label>
            <Input data-testid="devis-new-client-ville" value={form.ville} onChange={(e) => update("ville", e.target.value)} placeholder="Ville" className="bg-[#0B0F17] border-[#1E293B]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-400 text-xs">Téléphone</Label>
            <Input data-testid="devis-new-client-telephone" value={form.telephone} onChange={(e) => update("telephone", e.target.value)} placeholder="06 00 00 00 00" className="bg-[#0B0F17] border-[#1E293B]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-400 text-xs">Email</Label>
            <Input type="email" data-testid="devis-new-client-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="client@exemple.fr" className="bg-[#0B0F17] border-[#1E293B]" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} className="text-slate-400">Annuler</Button>
          <Button data-testid="devis-new-client-save" onClick={create} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">
            <UserPlus className="h-4 w-4" />
            {saving ? "Création..." : "Créer le client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
